/**
 * ArtsSubmit — /arts/submit
 * Quick form to add an artist, musician, gallery, festival, or any
 * other Arts entity. AI biography draft assist via Claude Sonnet 4.5.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowLeft, PenSquare } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ArtsSubmit() {
  const nav  = useNavigate();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [form, setForm] = useState({
    section:   sp.get("section")  || "artists",
    category:  sp.get("category") || "",
    name:      "",
    tagline:   "",
    short_bio: "",
    long_bio:  "",
    country:   "",
    city:      "",
    born:      "",
    website:   "",
    contact_email: "",
    photo_url: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const m = await http.get("/arts/meta").then(r => r.data);
        setMeta(m);
      } catch { setMeta(null); }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const categories = useMemo(
    () => (meta?.section_categories?.[form.section] || []),
    [meta, form.section],
  );

  const draftBio = async () => {
    if (!form.name) { toast.error("Enter a name first"); return; }
    setAiBusy(true);
    try {
      // Create a minimal draft first so we have a slug to hit ai-biography with.
      const created = await http.post("/arts/entities", {
        section:   form.section,
        category:  form.category || undefined,
        name:      form.name,
        tagline:   form.tagline || undefined,
        short_bio: form.short_bio || undefined,
        country:   form.country || undefined,
        city:      form.city || undefined,
        born:      form.born || undefined,
      }).then(r => r.data);
      const r = await http.post(`/arts/entities/${created.slug}/ai-biography`, { tone: "warm" }).then(r => r.data);
      set("long_bio", r.draft || "");
      // Save the long_bio onto the just-created record via a follow-up POST
      // is not defined; leave the draft in the form for the user to review
      // and re-submit. Delete the draft entity so we don't leave a stub if
      // the user abandons the flow.
      try { await http.delete(`/arts/entities/${created.slug}`); } catch { /* ignore */ }
      toast.success("Biography drafted — review and edit below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to add a profile"); nav("/login?next=/arts/submit"); return; }
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
      const created = await http.post("/arts/entities", payload).then(r => r.data);
      toast.success("Profile created");
      nav(`/arts/${created.section}/${created.slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the profile");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent";
  const border   = { borderColor: "var(--wofy-border)" };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="arts-submit">
      <Link to="/arts" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Arts hub
      </Link>
      <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>ADD TO ARTS</div>
      <h1 className="font-display text-3xl md:text-4xl mb-1">Create a profile</h1>
      <p className="opacity-80 mb-6">Fill in the essentials — you can polish later. All fields except name and section are optional.</p>

      <form onSubmit={submit} className="wofy-card !p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Section *</span>
            <select value={form.section} onChange={(e) => { set("section", e.target.value); set("category", ""); }}
                    className={inputCls} style={border} data-testid="arts-form-section">
              {(meta?.sections || []).map(s => (
                <option key={s.slug} value={s.slug}>{s.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Category</span>
            <select value={form.category} onChange={(e) => set("category", e.target.value)}
                    className={inputCls} style={border} data-testid="arts-form-category">
              <option value="">— any —</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Name *</span>
          <input value={form.name} onChange={(e) => set("name", e.target.value)}
                 className={inputCls} style={border} required data-testid="arts-form-name"
                 placeholder="e.g. Amrita Sher-Gil, Berklee College of Music, Sundance Film Festival" />
        </label>

        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Tagline</span>
          <input value={form.tagline} onChange={(e) => set("tagline", e.target.value)}
                 className={inputCls} style={border} data-testid="arts-form-tagline"
                 placeholder="One-line description" />
        </label>

        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Short bio (1-2 sentences)</span>
          <textarea value={form.short_bio} onChange={(e) => set("short_bio", e.target.value)}
                    className={inputCls} style={border} rows={3} data-testid="arts-form-short-bio" />
        </label>

        <div className="rounded-lg border p-4"
             style={{ borderColor: "var(--wofy-primary)", background: "rgba(139,92,246,0.06)" }}>
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              <span>Let AI draft a full biography from what you've entered</span>
            </div>
            <button type="button" onClick={draftBio} disabled={aiBusy || !form.name}
                    className="wofy-btn-ghost text-xs disabled:opacity-50" data-testid="arts-ai-draft">
              {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Draft with AI
            </button>
          </div>
          <p className="text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
            AI uses only the facts you've entered — always edit before publishing.
          </p>
        </div>

        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Full biography</span>
          <textarea value={form.long_bio} onChange={(e) => set("long_bio", e.target.value)}
                    className={inputCls} style={border} rows={8} data-testid="arts-form-long-bio" />
        </label>

        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Country</span>
            <input value={form.country} onChange={(e) => set("country", e.target.value)}
                   className={inputCls} style={border} data-testid="arts-form-country" />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">City</span>
            <input value={form.city} onChange={(e) => set("city", e.target.value)}
                   className={inputCls} style={border} data-testid="arts-form-city" />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Born (year or date)</span>
            <input value={form.born} onChange={(e) => set("born", e.target.value)}
                   className={inputCls} style={border} placeholder="1770" data-testid="arts-form-born" />
          </label>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Website</span>
            <input type="url" value={form.website} onChange={(e) => set("website", e.target.value)}
                   className={inputCls} style={border} placeholder="https://…" data-testid="arts-form-website" />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Contact email</span>
            <input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)}
                   className={inputCls} style={border} data-testid="arts-form-email" />
          </label>
        </div>

        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Photo URL</span>
          <input type="url" value={form.photo_url} onChange={(e) => set("photo_url", e.target.value)}
                 className={inputCls} style={border} placeholder="https://…" data-testid="arts-form-photo" />
        </label>

        <div className="pt-2 border-t flex items-center justify-end gap-3" style={border}>
          <Link to="/arts" className="wofy-btn-ghost" data-testid="arts-form-cancel">Cancel</Link>
          <button type="submit" disabled={busy} className="wofy-btn-primary" data-testid="arts-form-submit">
            {busy ? "Publishing…" : (<><PenSquare className="w-4 h-4" /> Publish</>)}
          </button>
        </div>
      </form>
    </div>
  );
}
